// =============================================================================
// Shared Picker Utilities
// Common utilities used across DatePicker and DateRangePicker components
// =============================================================================

import { formatDateToYMD, formatDateTimeToYMDHMS, padNumber } from "./calendar";

// =============================================================================
// Type Definitions
// =============================================================================

export type TimezoneValueType = "utc" | "local" | "custom";

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export type FieldType = "from" | "to";
export type GranularityType = "date" | "hour" | "minute" | "second";

// =============================================================================
// Error Message Constants
// =============================================================================

export const DATE_ERRORS = {
  INVALID_FORMAT: "Invalid format. Try: 2024-01-15, now-7d, yesterday, last week",
  BETWEEN: (min: string, max: string) => `Date must be between ${min} and ${max}`,
  MIN: (min: string) => `Date must be ${min} or later`,
  MAX: (max: string) => `Date must be ${max} or earlier`,
  FROM_AFTER_TO: "From date cannot be after to date",
  TO_BEFORE_FROM: "To date cannot be before from date",
} as const;

// =============================================================================
// Date Detection
// =============================================================================

/**
 * Check if a string is an absolute ISO date (starts with YYYY-MM-DD)
 * Used to distinguish ISO dates from relative expressions like "now-7d"
 */
export const isAbsoluteDate = (val: string): boolean => /^\d{4}-\d{2}-\d{2}/.test(val);

// =============================================================================
// Timezone Utilities
// =============================================================================

/**
 * Get the system's local timezone
 */
export const getSystemTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Resolve the effective timezone based on value and type
 */
export const resolveTimezone = (
  value?: string,
  type?: TimezoneValueType
): string => {
  if (type === "utc") return "UTC";
  if (type === "local") return getSystemTimezone();
  return value ?? "UTC";
};

// =============================================================================
// Tooltip Formatting
// =============================================================================

/**
 * Format a date for tooltip display with both UTC and local timezone info
 *
 * The interpretation depends on selected timezone:
 * - UTC selected: stored value IS UTC, derive local by adding offset
 * - Local selected: stored value IS LOCAL (what user typed), derive UTC by subtracting offset
 *
 * @param date - The date to format (stored as UTC string, e.g., 2026-01-08T08:26:24Z)
 * @param selectedTimezone - The currently selected timezone ("UTC" or local timezone string)
 */
export const formatTooltipWithTimezone = (date: Date, selectedTimezone?: string): string => {
  const isUTCSelected = selectedTimezone === "UTC";

  // Calculate timezone offset
  const offsetMinutes = -date.getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const offsetMins = Math.abs(offsetMinutes) % 60;
  const offsetSign = offsetMinutes >= 0 ? "+" : "-";
  const offsetStr = offsetMins > 0
    ? `UTC${offsetSign}${offsetHours}:${padNumber(offsetMins)}`
    : `UTC${offsetSign}${offsetHours}`;

  // Get the stored UTC components (what user typed, stored literally)
  const storedStr = formatDateTimeToYMDHMS(date);

  if (isUTCSelected) {
    // Stored value IS UTC - derive local by using browser's local time methods
    const localYear = date.getFullYear();
    const localMonth = padNumber(date.getMonth() + 1);
    const localDay = padNumber(date.getDate());
    const localHours = padNumber(date.getHours());
    const localMinutes = padNumber(date.getMinutes());
    const localSeconds = padNumber(date.getSeconds());
    const localStr = `${localYear}-${localMonth}-${localDay} ${localHours}:${localMinutes}:${localSeconds}`;

    return `UTC: ${storedStr}\nLocal (${offsetStr}): ${localStr}`;
  } else {
    // Stored value IS LOCAL - derive UTC by subtracting offset
    const offsetMs = offsetMinutes * 60 * 1000;
    const derivedUTCDate = new Date(date.getTime() - offsetMs);
    const derivedUTCStr = formatDateTimeToYMDHMS(derivedUTCDate);

    return `UTC: ${derivedUTCStr}\nLocal (${offsetStr}): ${storedStr}`;
  }
};

// =============================================================================
// Time Utilities
// =============================================================================

/**
 * Extract time string from a Date object based on granularity
 * Returns HH:mm or HH:mm:ss depending on granularity
 */
export const extractTimeFromDate = (
  date: Date,
  granularity: "hour" | "minute" | "second"
): string => {
  const hours = padNumber(date.getUTCHours());
  const minutes = padNumber(date.getUTCMinutes());
  const seconds = padNumber(date.getUTCSeconds());

  if (granularity === "second") return `${hours}:${minutes}:${seconds}`;
  if (granularity === "hour") return `${hours}:00`;
  return `${hours}:${minutes}`;
};

/**
 * Combine a date string (YYYY-MM-DD) and time string (HH:mm or HH:mm:ss) into ISO string
 */
export const combineDateAndTime = (
  dateStr: string,
  timeStr: string
): string => {
  const parts = timeStr.split(":").map(Number);
  const hours = padNumber(parts[0] || 0);
  const minutes = padNumber(parts[1] || 0);
  const seconds = padNumber(parts[2] || 0);
  return `${dateStr}T${hours}:${minutes}:${seconds}Z`;
};

/**
 * Create a Date object set to midnight UTC for a given date
 */
export const toMidnightUTC = (date: Date): Date => {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
};

/**
 * Format a Date object based on granularity
 * - "date": YYYY-MM-DD
 * - "hour" | "minute": YYYY-MM-DD HH:mm
 * - "second": YYYY-MM-DD HH:mm:ss
 */
export const formatDateWithGranularity = (
  date: Date,
  granularity: "date" | "hour" | "minute" | "second"
): string => {
  if (granularity === "date") {
    return formatDateToYMD(date);
  }
  if (granularity === "second") {
    return formatDateTimeToYMDHMS(date);
  }
  // hour or minute
  const datePart = formatDateToYMD(date);
  const hours = padNumber(date.getUTCHours());
  const minutes = padNumber(date.getUTCMinutes());
  return `${datePart} ${hours}:${minutes}`;
};

// =============================================================================
// Validation Utilities
// =============================================================================

/**
 * Validate a date against min/max constraints
 * Uses string comparison (YYYY-MM-DD format)
 */
export const validateDateConstraints = (
  dateStr: string,
  minDate?: string,
  maxDate?: string
): ValidationResult => {
  const isBeforeMin = minDate && dateStr < minDate;
  const isAfterMax = maxDate && dateStr > maxDate;

  if (isBeforeMin || isAfterMax) {
    if (minDate && maxDate) {
      return { isValid: false, error: DATE_ERRORS.BETWEEN(minDate, maxDate) };
    }
    if (isBeforeMin) {
      return { isValid: false, error: DATE_ERRORS.MIN(minDate!) };
    }
    return { isValid: false, error: DATE_ERRORS.MAX(maxDate!) };
  }

  return { isValid: true };
};

/**
 * Validate that from/to dates are in correct order
 * Takes already-resolved date strings (YYYY-MM-DD)
 */
export const validateRangeOrder = (
  newDateStr: string,
  otherDateStr: string | null,
  editingField: FieldType
): ValidationResult => {
  if (!otherDateStr) {
    return { isValid: true };
  }

  if (editingField === "from") {
    // Editing from field - check it's not after to
    if (newDateStr > otherDateStr) {
      return { isValid: false, error: DATE_ERRORS.FROM_AFTER_TO };
    }
  } else {
    // Editing to field - check it's not before from
    if (newDateStr < otherDateStr) {
      return { isValid: false, error: DATE_ERRORS.TO_BEFORE_FROM };
    }
  }

  return { isValid: true };
};

/**
 * Get default time string for a field based on granularity
 * "from" fields default to start of day (00:00)
 * "to" fields default to end of day (23:59)
 */
export const getDefaultTimeForField = (
  fieldType: FieldType,
  granularity: GranularityType
): string => {
  if (granularity === "date") return "";

  const isFrom = fieldType === "from";

  if (granularity === "second") {
    return isFrom ? "00:00:00" : "23:59:59";
  }
  // hour or minute
  return isFrom ? "00:00" : "23:59";
};

/**
 * Resolve a value (absolute date or expression) to a display date string
 * Returns YYYY-MM-DD format or null if invalid
 *
 * Note: This function requires the `parse` function from the parent module.
 * To avoid circular imports, pass it as a parameter.
 */
export const resolveToDateString = (
  value: string | null,
  timezone: string,
  parseFn: (input: string, options: { pickerType: string; timezone: string }) => { resolved?: Date; isValid: boolean }
): string | null => {
  if (!value) return null;

  if (isAbsoluteDate(value)) {
    return formatDateToYMD(new Date(value));
  }

  const parsed = parseFn(value, { pickerType: "single", timezone });
  return parsed.resolved ? formatDateToYMD(parsed.resolved) : null;
};

/**
 * Convert a date value when timezone changes with "updateDisplay" behavior.
 *
 * With "updateDisplay", the same instant in time should be shown in the new timezone.
 * This means the stored value (which uses local-as-UTC format for non-UTC timezones)
 * needs to be converted.
 *
 * Example: 14:00 UTC → switching to Local (UTC+3) → should show 17:00 local
 * - Old value: "2026-01-15T14:00:00Z" (14:00 UTC)
 * - New value: "2026-01-15T17:00:00Z" (17:00 in local-as-UTC format)
 *
 * @param value - The current value (ISO string or expression)
 * @param fromTimezone - The old timezone ("UTC" or local timezone string)
 * @param toTimezone - The new timezone ("UTC" or local timezone string)
 * @returns The converted value, or the original value if no conversion needed
 */
export const convertValueForTimezoneSwitch = (
  value: string | null,
  fromTimezone: string,
  toTimezone: string
): string | null => {
  // No value or same timezone - no conversion needed
  if (!value || fromTimezone === toTimezone) return value;

  // Only convert absolute dates, not expressions
  if (!isAbsoluteDate(value)) return value;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  // Get timezone offset in milliseconds
  const offsetMinutes = -date.getTimezoneOffset();
  const offsetMs = offsetMinutes * 60 * 1000;

  let newDate: Date;

  if (fromTimezone === "UTC" && toTimezone !== "UTC") {
    // UTC → Local: The instant 14:00 UTC should show as 17:00 local (for UTC+3)
    // In local-as-UTC format, we store 17:00 as "17:00Z"
    // So we ADD the offset: 14:00Z + 3h = 17:00Z (local-as-UTC)
    newDate = new Date(date.getTime() + offsetMs);
  } else if (fromTimezone !== "UTC" && toTimezone === "UTC") {
    // Local → UTC: The instant shown as 17:00 local is actually 14:00 UTC
    // Local-as-UTC "17:00Z" represents 17:00 local = 14:00 UTC
    // So we SUBTRACT the offset: 17:00Z - 3h = 14:00Z (actual UTC)
    newDate = new Date(date.getTime() - offsetMs);
  } else {
    // Both non-UTC (shouldn't happen in our system, but handle gracefully)
    return value;
  }

  return `${formatDateTimeToYMDHMS(newDate)}Z`;
};
